import { onSensitiveMemoryCacheClear } from '../../runtime/sensitive-cache-lifecycle';

type WorkspaceHomeReadMode = 'off' | 'shadow' | 'cutover';

type WorkspaceHomeReadEnvironment = Readonly<{
  VITE_WORKSPACE_HOME_READ_MODE?: string;
}>;

type LegacyHomePort<T> = Readonly<{
  load(selectedScopeId: string | null): Promise<T | null>;
}>;

export type WorkspaceHomeReadPort = Readonly<{
  load(selectedScopeId: string | null): Promise<unknown>;
}>;

const COMPOSITE_FRESH_MS = 10_000;
const compositeReads = new Map<string, { settledAt: number | null; value: Promise<unknown> }>();
export const clearWorkspaceHomeReadMemoryCache = () => compositeReads.clear();
onSensitiveMemoryCacheClear(clearWorkspaceHomeReadMemoryCache);

/** Coalesces the work projection, board card, and notification badge onto one session read. */
export function loadWorkspaceHomeReadOnce(
  port: WorkspaceHomeReadPort,
  selectedScopeId: string | null,
): Promise<unknown> {
  const key = selectedScopeId ?? 'saved-default';
  const existing = compositeReads.get(key);
  if (
    existing &&
    (existing.settledAt === null || Date.now() - existing.settledAt <= COMPOSITE_FRESH_MS)
  )
    return existing.value;
  const value = port
    .load(selectedScopeId)
    .then((result) => {
      const current = compositeReads.get(key);
      if (current?.value === value) current.settledAt = Date.now();
      return result;
    })
    .catch((error) => {
      const current = compositeReads.get(key);
      if (current?.value === value) compositeReads.delete(key);
      throw error;
    });
  compositeReads.set(key, { settledAt: null, value });
  return value;
}

export type WorkspaceHomeReadShadowEvidence = Readonly<{
  bffAccepted: boolean;
  bffContentHash: string | null;
  legacyAvailable: boolean;
}>;

export const WORKSPACE_HOME_READ_SHADOW_EVENT = 'kop:workspace-home-read-shadow';

const record = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);
const sha256 = (value: unknown): value is string =>
  typeof value === 'string' && /^[a-f0-9]{64}$/u.test(value);
const sourceEnvelope = (value: unknown): value is Record<string, unknown> =>
  record(value) &&
  (value.status === 'complete' || value.status === 'partial' || value.status === 'unavailable') &&
  typeof value.checkedAt === 'string' &&
  Number.isFinite(Date.parse(value.checkedAt)) &&
  (value.cursor === null || typeof value.cursor === 'string') &&
  (value.reason === null || typeof value.reason === 'string') &&
  sha256(value.contentHash) &&
  (value.status === 'unavailable' ? value.data === null : value.data != null);

export function workspaceHomeReadMode(
  environment: WorkspaceHomeReadEnvironment,
): WorkspaceHomeReadMode {
  return environment.VITE_WORKSPACE_HOME_READ_MODE === 'shadow' ||
    environment.VITE_WORKSPACE_HOME_READ_MODE === 'cutover'
    ? environment.VITE_WORKSPACE_HOME_READ_MODE
    : 'off';
}

function observedProjection<T>(
  value: unknown,
  requestedScopeId: string | null,
  decodeProjection: (candidate: unknown) => T | null,
): Readonly<{ projection: T; contentHash: string; complete: boolean }> | null {
  if (!record(value) || value.contractVersion !== 'workspace-home-read.v2') return null;
  const selectedScopeId = record(value.scope) ? value.scope.selectedScopeId : null;
  const siteId = record(value.scope) ? value.scope.siteId : null;
  const scopedContractor = typeof selectedScopeId === 'string'
    ? /^site:([A-Za-z0-9][A-Za-z0-9._:-]{0,127})\/contractor:([A-Za-z0-9][A-Za-z0-9._:-]{0,127})$/u.exec(selectedScopeId)
    : null;
  if (
    !record(value.scope) ||
    value.scope.tenantId !== 'kop' ||
    typeof siteId !== 'string' ||
    !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u.test(siteId) ||
    (selectedScopeId !== `site:${siteId}` &&
      scopedContractor?.[1] !== siteId &&
      !(
        typeof selectedScopeId === 'string' &&
        /^contractor:[A-Za-z0-9][A-Za-z0-9._:-]{0,255}$/u.test(selectedScopeId)
      )) ||
    (requestedScopeId !== null && selectedScopeId !== requestedScopeId)
  )
    return null;
  if (
    value.preferenceIssue !== null &&
    value.preferenceIssue !== 'PREFERENCES_REQUIRED' &&
    value.preferenceIssue !== 'HOME_POLICY_UNAVAILABLE'
  )
    return null;
  if (
    !record(value.limits) ||
    value.limits.boardRows !== 6 ||
    value.limits.notificationUnread !== 500 ||
    value.limits.authorityUnread !== 500 ||
    value.limits.approvalPerStatus !== 30 ||
    value.limits.approvalDelegations !== 100 ||
    value.limits.findings !== 100 ||
    value.limits.dueActions !== 10
  )
    return null;
  if (typeof value.checkedAt !== 'string' || !Number.isFinite(Date.parse(value.checkedAt)))
    return null;
  if (!sha256(value.contentHash) || !record(value.sources)) return null;
  if (
    !sourceEnvelope(value.sources.workHomeProjection) ||
    !sourceEnvelope(value.sources.board) ||
    !sourceEnvelope(value.sources.notifications) ||
    !sourceEnvelope(value.sources.authorityNotifications) ||
    !sourceEnvelope(value.sources.approvals) ||
    !sourceEnvelope(value.sources.findings)
  )
    return null;
  const board = value.sources.board;
  if (
    board.data !== null &&
    (!record(board.data) ||
      !Array.isArray(board.data.items) ||
      board.data.items.length > 6 ||
      board.data.limit !== 6)
  )
    return null;
  for (const notificationSource of [
    value.sources.notifications,
    value.sources.authorityNotifications,
  ])
    if (
      notificationSource.data !== null &&
      (!record(notificationSource.data) ||
        !Number.isSafeInteger(notificationSource.data.unreadCount) ||
        Number(notificationSource.data.unreadCount) < 0 ||
        Number(notificationSource.data.unreadCount) > 500 ||
        notificationSource.data.limit !== 500 ||
        typeof notificationSource.data.complete !== 'boolean')
    )
      return null;
  const approvals = value.sources.approvals;
  if (
    approvals.data !== null &&
    (!record(approvals.data) ||
      !Number.isSafeInteger(approvals.data.pendingCount) ||
      Number(approvals.data.pendingCount) < 0 ||
      Number(approvals.data.pendingCount) > 60 ||
      !Array.isArray(approvals.data.items) ||
      approvals.data.items.length !== approvals.data.pendingCount ||
      approvals.data.perStatusLimit !== 30 ||
      approvals.data.delegationLimit !== 100 ||
      typeof approvals.data.complete !== 'boolean')
  )
    return null;
  const findings = value.sources.findings;
  if (
    findings.data !== null &&
    (!record(findings.data) ||
      !Number.isSafeInteger(findings.data.unresolvedCount) ||
      Number(findings.data.unresolvedCount) < 0 ||
      Number(findings.data.unresolvedCount) > 100 ||
      !Array.isArray(findings.data.items) ||
      findings.data.items.length !== findings.data.unresolvedCount ||
      findings.data.limit !== 100 ||
      typeof findings.data.complete !== 'boolean')
  )
    return null;
  const source = value.sources.workHomeProjection;
  if ((source.status !== 'complete' && source.status !== 'partial') || source.data == null)
    return null;
  const projection = decodeProjection(source.data);
  return projection
    ? {
        projection,
        contentHash: value.contentHash,
        complete: source.status === 'complete',
      }
    : null;
}

function observedSource<T>(
  value: unknown,
  selectedScopeId: string | null,
  name: 'board' | 'authorityNotifications',
  decode: (candidate: unknown) => T | null,
  acceptPartial: boolean,
): T | null {
  if (!observedProjection(value, selectedScopeId, () => true)) return null;
  const sources = (value as { sources: Record<string, unknown> }).sources;
  const source = sources[name];
  if (!sourceEnvelope(source) || source.data === null) return null;
  if (source.status !== 'complete' && !(acceptPartial && source.status === 'partial')) return null;
  return decode(source.data);
}

/** Shared source cutover: a malformed/partial count falls back; a valid partial board page is usable. */
export async function loadWorkspaceHomeSourceWithFallback<T>({
  environment,
  selectedScopeId,
  legacy,
  bff,
  source,
  decode,
  acceptPartial = false,
}: Readonly<{
  environment: WorkspaceHomeReadEnvironment;
  selectedScopeId: string | null;
  legacy: () => Promise<T | null>;
  bff: WorkspaceHomeReadPort;
  source: 'board' | 'authorityNotifications';
  decode: (candidate: unknown) => T | null;
  acceptPartial?: boolean;
}>): Promise<T | null> {
  const mode = workspaceHomeReadMode(environment);
  if (mode === 'off') return legacy();
  if (mode === 'cutover') {
    try {
      const accepted = observedSource(
        await loadWorkspaceHomeReadOnce(bff, selectedScopeId),
        selectedScopeId,
        source,
        decode,
        acceptPartial,
      );
      if (accepted !== null) return accepted;
    } catch {
      // The retained read-only endpoint is the only fallback.
    }
    return legacy();
  }
  const [, legacyResult] = await Promise.allSettled([
    loadWorkspaceHomeReadOnce(bff, selectedScopeId),
    legacy(),
  ]);
  return legacyResult.status === 'fulfilled' ? legacyResult.value : null;
}

/** Emits only bounded parity metadata; no identity, board content, or notification payload. */
export function publishWorkspaceHomeReadShadowEvidence(
  evidence: WorkspaceHomeReadShadowEvidence,
): void {
  if (typeof window === 'undefined' || typeof CustomEvent === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<WorkspaceHomeReadShadowEvidence>(WORKSPACE_HOME_READ_SHADOW_EVENT, {
      detail: evidence,
    }),
  );
}

/**
 * Opt-in seam only. `off` preserves the existing endpoint exactly; `shadow` validates the bounded
 * BFF envelope but returns legacy data and emits metadata-only evidence. `cutover` accepts a
 * structurally valid v2 projection even when one bounded inner source is explicitly `partial`;
 * consumers must keep that inner source unavailable rather than presenting its lower bound as an
 * exact count. Legacy is invoked only for a malformed envelope or transport failure. Both ports are
 * read-only.
 */
export async function loadWorkspaceHomeWithFallback<T>({
  environment,
  selectedScopeId,
  legacy,
  bff,
  decodeProjection,
  onShadowEvidence,
}: Readonly<{
  environment: WorkspaceHomeReadEnvironment;
  selectedScopeId: string | null;
  legacy: LegacyHomePort<T>;
  bff: WorkspaceHomeReadPort;
  decodeProjection: (candidate: unknown) => T | null;
  onShadowEvidence?: (evidence: WorkspaceHomeReadShadowEvidence) => void;
}>): Promise<T | null> {
  const mode = workspaceHomeReadMode(environment);
  if (mode === 'off') return legacy.load(selectedScopeId);

  if (mode === 'cutover') {
    try {
      const accepted = observedProjection(
        await loadWorkspaceHomeReadOnce(bff, selectedScopeId),
        selectedScopeId,
        decodeProjection,
      );
      if (accepted) return accepted.projection;
    } catch {
      // A cutover read is fail-open only to the retained read-only legacy endpoint.
    }
    return legacy.load(selectedScopeId);
  }

  const [bffResult, legacyResult] = await Promise.allSettled([
    loadWorkspaceHomeReadOnce(bff, selectedScopeId),
    legacy.load(selectedScopeId),
  ]);
  const accepted =
    bffResult.status === 'fulfilled'
      ? observedProjection(bffResult.value, selectedScopeId, decodeProjection)
      : null;
  const legacyValue = legacyResult.status === 'fulfilled' ? legacyResult.value : null;
  onShadowEvidence?.({
    bffAccepted: accepted !== null,
    bffContentHash:
      bffResult.status === 'fulfilled' && record(bffResult.value)
        ? sha256(bffResult.value.contentHash)
          ? bffResult.value.contentHash
          : null
        : null,
    legacyAvailable: legacyValue !== null,
  });
  return legacyValue;
}
